import { useCallback } from "react";
import {
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import { InteractionRequiredAuthError, SilentRequest } from "@azure/msal-browser";
import { loginRequest, msalInstance } from "./msalConfig";
import type { UserInfo } from "../types/dashboard";

export interface UseAuthReturn {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  getToken: () => Promise<string>;
  logout: () => void;
}

/**
 * Custom hook that abstracts MSAL authentication operations.
 * Provides user info, token acquisition, and logout functionality.
 */
export function useAuth(): UseAuthReturn {
  const { accounts, inProgress, instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const activeAccount = accounts[0] ?? null;

  const user: UserInfo | null = activeAccount
    ? {
        email: activeAccount.username,
        name: activeAccount.name ?? activeAccount.username,
      }
    : null;

  const isLoading = inProgress !== "none";

  const getToken = useCallback(async (): Promise<string> => {
    if (!activeAccount) {
      throw new Error("No active account. User must be authenticated.");
    }

    const silentRequest: SilentRequest = {
      ...loginRequest,
      account: activeAccount,
    };

    try {
      const response = await msalInstance.acquireTokenSilent(silentRequest);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Fall back to interactive token acquisition
        const response = await instance.acquireTokenPopup(silentRequest);
        return response.accessToken;
      }
      throw error;
    }
  }, [activeAccount, instance]);

  const logout = useCallback((): void => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance]);

  return {
    user,
    isAuthenticated,
    isLoading,
    getToken,
    logout,
  };
}
